// ============================================
// MyReviews.jsx - MY REVIEW SUBMISSIONS
// Save as: src/MyReviews.jsx
// ============================================

import React from 'react';
import { 
  Star, 
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink
} from 'lucide-react';

const MyReviews = ({
  currentUser,
  books,
  reviews
}) => {
  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const getUserReviews = () => {
    if (!reviews || !Array.isArray(reviews)) return [];
    if (!currentUser?.id) return [];
    
    return reviews.filter(r => r.reviewerId === currentUser.id);
  };

  const getReviewStatusColor = status => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReviewStatusIcon = status => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  // ============================================
  // RENDER
  // ============================================

  const myReviews = getUserReviews();

  // Sort reviews: pending first, then by date
  const sortedReviews = [...myReviews].sort((a, b) => {
    // Pending reviews first
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    
    // Then by submission date (newest first)
    return new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0);
  });

  // Count by status
  const statusCounts = {
    pending: myReviews.filter(r => r.status === 'pending').length,
    approved: myReviews.filter(r => r.status === 'approved').length,
    rejected: myReviews.filter(r => r.status === 'rejected').length
  };

  return (
    <div>
      {/* ============================================ */}
      {/* HEADER WITH STATS */}
      {/* ============================================ */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-4">My Review Submissions</h2>
        
        {myReviews.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-2xl font-bold text-yellow-800">
                {statusCounts.pending}
              </div>
              <div className="text-xs text-yellow-700">Pending</div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-800">
                {statusCounts.approved}
              </div>
              <div className="text-xs text-green-700">Approved</div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-2xl font-bold text-red-800">
                {statusCounts.rejected}
              </div>
              <div className="text-xs text-red-700">Rejected</div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* REVIEWS LIST */}
      {/* ============================================ */}
      {myReviews.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium mb-2">No reviews submitted yet</p>
          <p className="text-sm text-gray-500">
            Claim a book from the Browse page to start reviewing!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedReviews.map(review => {
            const book = books.find(b => b.id === review.bookId);
            
            return (
              <div 
                key={review.id} 
                className={`bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition ${
                  review.status === 'pending' ? 'border-2 border-yellow-300' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-800">
                      {book?.title || 'Unknown Book'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      by {book?.author || 'Unknown Author'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Submitted: {new Date(review.submittedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  
                  {/* Status badge */}
                  <div className="flex flex-col items-end gap-2">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${getReviewStatusColor(review.status)}`}>
                      {getReviewStatusIcon(review.status)}
                      {review.status.toUpperCase()}
                    </div>
                    
                    {/* Quality score */}
                    {review.qualityScore && (
                      <div className="text-sm font-bold text-green-600">
                        Score: {review.qualityScore}/10
                      </div>
                    )}
                  </div>
                </div>

                {/* Rating stars */}
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < review.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="ml-2 text-sm font-semibold text-gray-700">
                    {review.rating}/5
                  </span>
                </div>

                {/* Review text */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {review.reviewText}
                  </p>
                  {review.wordCount && (
                    <p className="text-xs text-gray-500 mt-2">
                      {review.wordCount} words
                    </p>
                  )}
                </div>

                {/* Amazon review link */}
                <div className="flex items-center justify-between">
                  <a
                    href={review.amazonReviewLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on Amazon
                  </a>
                  
                  {/* Reviewer name on Amazon */}
                  {review.amazonReviewerName && (
                    <span className="text-xs text-gray-500">
                      Posted as: {review.amazonReviewerName}
                    </span>
                  )}
                </div>

                {/* Pending notice */}
                {review.status === 'pending' && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      ⏳ <strong>Awaiting admin approval.</strong> You'll receive your coins once approved (usually within 24 hours).
                    </p>
                  </div>
                )}

                {/* Approved notice */}
                {review.status === 'approved' && review.approvedAt && (
                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-800">
                       <strong>Approved on {new Date(review.approvedAt).toLocaleDateString()}</strong>
                      {book?.coinsRequired && ` - Earned ${book.coinsRequired} coins!`}
                    </p>
                  </div>
                )}

                {/* Rejected notice */}
                {review.status === 'rejected' && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs text-red-800">
                      ❌ <strong>Review rejected.</strong> Please revise and resubmit with more detail.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================ */}
      {/* FOOTER TIP */}
      {/* ============================================ */}
      {myReviews.length > 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Pro Tip:</strong> Write detailed reviews (50+ words) with specific examples to earn quality bonuses and higher approval rates!
          </p>
        </div>
      )}
    </div>
  );
};

export default MyReviews;